import Alert from "antd/lib/alert";
import Typography from "antd/lib/typography";
import React, { Fragment, useContext, useEffect } from "react";
import Helmet from "react-helmet";
import LayoutContext from "~context/Layout";

function HowItWorksPage(): JSX.Element {
    const layout = useContext(LayoutContext);

    useEffect(() => {
        if (layout.data && layout.actions) {
            if (layout.data.header !== true) layout.actions.updateHeader(true);
            if (layout.data.footer !== true) layout.actions.updateFooter(true);
        }
    }, [layout.data, layout.actions]);

    return (
        <Fragment>
            <Helmet>
                <title>How it Works - Twenty4 Tan Co.</title>
            </Helmet>

            <div className="container-xl my-50 px-50 text-center-md">
                <Typography.Title className="font-weight-400" level={2}>
                    How does it work?
                </Typography.Title>
                <Typography.Paragraph>
                    Getting started only takes a few minutes. Follow these
                    steps from account setup to walking in the door.
                </Typography.Paragraph>

                <Typography.Title className="font-weight-400 mt-25" level={4}>
                    1. Create Your Account
                </Typography.Title>
                <Typography.Paragraph>
                    <ol>
                        <li>Click &apos;Sign In&apos; in the top-right corner.</li>
                        <li>
                            Click &apos;Create an Account&apos; below the login
                            area.
                        </li>
                        <li>
                            Enter your first name, last name, date of birth,
                            email address, and password.
                        </li>
                        <li>
                            Read the terms, check both agreement boxes, and
                            click &apos;Register&apos;.
                        </li>
                    </ol>
                </Typography.Paragraph>

                <Alert
                    className="mb-15"
                    message="If you are under 18"
                    description="A parent or legal guardian must create and manage the account for membership access."
                    showIcon={true}
                    type="info"
                />

                <Typography.Title className="font-weight-400 mt-25" level={4}>
                    2. Verify Your Email
                </Typography.Title>
                <Typography.Paragraph>
                    <ol>
                        <li>
                            Open the verification email from Twenty4 Tan Co.
                            and click the verification link.
                        </li>
                        <li>
                            Return to the site and sign in with your email and
                            password.
                        </li>
                    </ol>
                </Typography.Paragraph>

                <Alert
                    className="mb-15"
                    message="Didn&apos;t get the email?"
                    description="Please wait a few minutes and check your Spam or Junk folder."
                    showIcon={true}
                    type="warning"
                />

                <Typography.Title className="font-weight-400 mt-25" level={4}>
                    3. Start Membership
                </Typography.Title>
                <Typography.Paragraph>
                    <ol>
                        <li>
                            Click your email in the top-right menu and open
                            &apos;Membership&apos;.
                        </li>
                        <li>
                            Add a payment method (credit or debit card) using
                            &apos;Add+&apos;.
                        </li>
                        <li>
                            Click &apos;Subscribe&apos;, then confirm in the pop-up
                            window.
                        </li>
                    </ol>
                </Typography.Paragraph>

                <Typography.Title className="font-weight-400 mt-25" level={4}>
                    4. Get Your Access Code
                </Typography.Title>
                <Typography.Paragraph>
                    <ol>
                        <li>
                            Open the top-right menu and click
                            &apos;Access Code&apos;.
                        </li>
                        <li>
                            You&apos;ll see your 4-digit code to use at the door.
                        </li>
                    </ol>
                </Typography.Paragraph>

                <Typography.Title className="font-weight-400 mt-25" level={4}>
                    5. Enter The Salon
                </Typography.Title>
                <Typography.Paragraph>
                    At the salon entrance, enter your 4-digit code on the
                    keypad and press the red button. The door should unlock in
                    about 2 seconds. Each room includes bed usage instructions.
                </Typography.Paragraph>

                <Alert
                    className="mb-0"
                    message="Payment and access reminders"
                    description="If payment fails or is past due, access code entry is paused until payment is collected. You can update your card on Membership, and view dates, amounts, and card details in Subscription & Payment History."
                    showIcon={true}
                    type="warning"
                />
            </div>
        </Fragment>
    );
}

export default HowItWorksPage;
